import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Landing from "./pages/Landing";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PendingVerification from "./pages/PendingVerification";
import About from "./pages/About";
import ComingSoon from "./pages/ComingSoon";
import Offers from "./pages/Offers";
import ProgramDetails from "./pages/ProgramDetails";
import ProgramLevelSelect from "./pages/ProgramLevelSelect";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Lessons from "./pages/Lessons";
import LessonDetail from "./pages/LessonDetail";
import LessonEditor from "./pages/LessonEditor";
import TeacherTimetable from "./pages/TeacherTimetable";
import Resources from "./pages/Resources";
import Profile from "./pages/Profile";
import PackDetails from "./pages/PackDetails";
import TeacherPublicProfile from "./pages/TeacherPublicProfile";
import Students from "./pages/Students";
import Tests from "./pages/Tests";
import Messages from "./pages/Messages";
import Homeworks from "./pages/Homeworks";
import HomeworkEditor from "./pages/HomeworkEditor";
import HomeworkTake from "./pages/HomeworkTake";
import HomeworkSubmissions from "./pages/HomeworkSubmissions";
import ProgramTestNew from "./pages/ProgramTestNew";
import ProgramTestEditor from "./pages/ProgramTestEditor";
import ProgramTestTake from "./pages/ProgramTestTake";
import ProgramTestSubmissions from "./pages/ProgramTestSubmissions";
import PlacementTestLevelSelect from "./pages/PlacementTestLevelSelect";
import PlacementTestTake from "./pages/PlacementTestTake";
import PlacementTestStart from "./pages/PlacementTestStart";
import Checkout from "./pages/Checkout";
import PaymentStatus from "./pages/PaymentStatus";
import Header from "./components/Header/Header";
import Sidebar from "./components/Sidebar/Sidebar";
import ChatDock from "./components/ChatDock/ChatDock";
import TableLabels from "./components/TableLabels";
import Footer from "./components/Footer/Footer";
import { AuthProvider } from "./context/AuthContext";
import {
  I18nProvider,
  getStoredLanguage,
  useTranslation,
} from "./context/I18nContext";
import { initI18n } from "./i18n";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestRoute from "./components/GuestRoute";
import "./App.css";
import logoIcon from "./assets/landing/logo icon2.png";
import { useAuth } from "./context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

const initialLanguage =
  typeof window !== "undefined" ? getStoredLanguage() : undefined;

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "575118756826-r9oi82e0d673lg5isjeshp6j4vd62p1v.apps.googleusercontent.com";

// Initialize i18n before rendering to prevent changeLanguage on an uninitialized instance.
initI18n(initialLanguage);

function RouteHistoryTracker() {
  const location = useLocation();

  useEffect(() => {
    try {
      const next = `${location.pathname}${location.search}`;
      const currentKey = "casaclass:lastRoute";
      const prevKey = "casaclass:prevRoute";

      const current = sessionStorage.getItem(currentKey);
      if (current && current !== next) {
        sessionStorage.setItem(prevKey, current);
      }
      sessionStorage.setItem(currentKey, next);
    } catch (e) {
      // Ignore storage errors (e.g. disabled storage).
    }

    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, location.search]);

  return null;
}

function AppShell() {
  const { t } = useTranslation();
  const { user, effectiveRole } = useAuth();
  const location = useLocation();
  const isStudent = effectiveRole === "student";
  const isUnverifiedTeacher =
    Boolean(user) && user.role === "teacher" && !user.is_verified;
  const isPlacementTestRunnerRoute = /^\/placement-test\/[A-Za-z0-9_-]+$/.test(
    location.pathname,
  );
  // Only treat `/tests/<id>` as a runner route. This prevents `/tests/new` from
  // incorrectly hiding the app shell.
  const isProgramTestRunnerRoute = /^\/tests\/\d+$/.test(location.pathname);
  const isTestRunnerRoute =
    isPlacementTestRunnerRoute || isProgramTestRunnerRoute;

  const isTeacherOrAdmin = ["teacher", "admin", "superadmin"].includes(
    effectiveRole,
  );
  const hideAppShellForRunner = isTestRunnerRoute && !isTeacherOrAdmin;

  const showSidebar =
    Boolean(user) &&
    !isStudent &&
    !isUnverifiedTeacher &&
    !hideAppShellForRunner;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((collapsed) => {
      try {
        window.localStorage.setItem("sidebarCollapsed", collapsed ? "0" : "1");
      } catch {
        // Not saved: the choice only lasts until the page is reloaded.
      }
      return !collapsed;
    });
  };

  if (process.env.NODE_ENV !== "production") {
    if (showSidebar && typeof Sidebar !== "function") {
      // eslint-disable-next-line no-console
      console.error(
        "Sidebar component is not a valid React component:",
        Sidebar,
      );
    }
    if (!isStudent && !isUnverifiedTeacher && typeof Header !== "function") {
      // eslint-disable-next-line no-console
      console.error("Header component is not a valid React component:", Header);
    }
  }

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div
      className={`app-root${isStudent ? " app-root--student" : ""}${
        showSidebar ? " app-root--with-sidebar" : ""
      }${sidebarOpen ? " app-root--sidebar-open" : ""}${
        showSidebar && sidebarCollapsed ? " app-root--sidebar-collapsed" : ""
      }`}
    >
      <RouteHistoryTracker />
      {showSidebar && typeof Sidebar === "function" && (
        <>
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={toggleSidebarCollapsed}
          />
          <button
            type="button"
            className={`app-sidebar__backdrop${sidebarOpen ? " is-visible" : ""}`}
            aria-label={t("sidebar.close")}
            onClick={() => setSidebarOpen(false)}
          />
        </>
      )}
      <div className="app-main">
        {!hideAppShellForRunner &&
          !isStudent &&
          !isUnverifiedTeacher &&
          typeof Header === "function" && (
            <Header
              showSidebarToggle={showSidebar}
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen((open) => !open)}
            />
          )}

        <Routes>
          <Route
            path="/"
            element={
              <GuestRoute>
                <Landing />
              </GuestRoute>
            }
          />
          <Route
            path="/placement-test/start"
            element={<PlacementTestStart />}
          />
          <Route
            path="/placement-test"
            element={<PlacementTestLevelSelect />}
          />
          <Route
            path="/placement-test/:testId"
            element={<PlacementTestTake />}
          />
          <Route
            path="/teachers/:teacherId"
            element={
              <GuestRoute>
                <TeacherPublicProfile />
              </GuestRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <GuestRoute>
                <Offers />
              </GuestRoute>
            }
          />
          <Route
            path="/programs"
            element={
              <ProtectedRoute>
                <ProgramDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:programId/select-level"
            element={<ProgramLevelSelect />}
          />
          <Route path="/programs/:programId" element={<ProgramDetails />} />
          <Route
            path="/about"
            element={
              <GuestRoute>
                <About />
              </GuestRoute>
            }
          />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <GuestRoute>
                <ForgotPassword />
              </GuestRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <GuestRoute>
                <ResetPassword />
              </GuestRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <GuestRoute>
                <Signup />
              </GuestRoute>
            }
          />
          <Route
            path="/pending-verification"
            element={
              <ProtectedRoute>
                <PendingVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/:packId"
            element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/return"
            element={
              <ProtectedRoute>
                <PaymentStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/failed"
            element={
              <ProtectedRoute>
                <PaymentStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Admin forcedTab="users" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/students"
            element={
              <ProtectedRoute>
                <Admin forcedTab="students" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/timetable"
            element={
              <ProtectedRoute>
                <Admin forcedTab="timetable" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pending-teachers"
            element={
              <ProtectedRoute>
                <Admin forcedTab="pendingTeachers" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/classes"
            element={
              <ProtectedRoute>
                <Admin forcedTab="classes" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/packs"
            element={
              <ProtectedRoute>
                <Admin forcedTab="packs" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/packs/:packId"
            element={
              <ProtectedRoute>
                <PackDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute>
                <Admin forcedTab="payments" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/teachers"
            element={
              <ProtectedRoute>
                <Admin forcedTab="teachers" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute>
                <Admin forcedTab="support" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/packs/:packId"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lessons"
            element={
              <ProtectedRoute>
                <Lessons />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timetable"
            element={
              <ProtectedRoute>
                <TeacherTimetable />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                <Resources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students"
            element={
              <ProtectedRoute>
                <Students />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:conversationId"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests"
            element={
              <ProtectedRoute>
                <Tests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/new"
            element={
              <ProtectedRoute>
                <ProgramTestNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/:testId"
            element={
              <ProtectedRoute>
                <ProgramTestTake />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/:testId/edit"
            element={
              <ProtectedRoute>
                <ProgramTestEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/:testId/submissions"
            element={
              <ProtectedRoute>
                <ProgramTestSubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeworks"
            element={
              <ProtectedRoute>
                <Homeworks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeworks/:homeworkId"
            element={
              <ProtectedRoute>
                <HomeworkTake />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeworks/:homeworkId/edit"
            element={
              <ProtectedRoute>
                <HomeworkEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeworks/:homeworkId/submissions"
            element={
              <ProtectedRoute>
                <HomeworkSubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lessons/new"
            element={
              <ProtectedRoute>
                <LessonEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lessons/:lessonId"
            element={
              <ProtectedRoute>
                <LessonDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lessons/:lessonId/edit"
            element={
              <ProtectedRoute>
                <LessonEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ComingSoon
                pageTitle={t("comingSoon.notFoundTitle")}
                message={t("comingSoon.notFoundMessage")}
                showPath
              />
            }
          />
        </Routes>
        {!user && !hideAppShellForRunner && <Footer />}
        {showSidebar ? <ChatDock /> : null}
        {showSidebar ? <TableLabels /> : null}
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    const ensureFaviconLink = () => {
      let favicon = document.querySelector("link[rel*='icon']");
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.type = "image/png";
      favicon.href = logoIcon;
    };

    ensureFaviconLink();
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      {/* Google's button follows the saved site language (else it uses the
          browser's language, e.g. Arabic on a French page). */}
      <GoogleOAuthProvider
        clientId={GOOGLE_CLIENT_ID}
        locale={initialLanguage}
      >
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <I18nProvider>
            <AuthProvider>
              <AppShell />
            </AuthProvider>
          </I18nProvider>
        </Router>
      </GoogleOAuthProvider>
    </I18nextProvider>
  );
}

export default App;
